import { ApiError } from '@eneco/dc/client/ApiError';
import { chamberOfCommerceSearch } from '@eneco/dc/services';
import { MachineConfig } from '@eneco/flows/types';
import {
  Customers_ChamberOfCommerce_ChamberOfCommerceSearchResponseModel,
  RequestModels_Customers_ChamberOfCommerce_ChamberOfCommerceSearchRequestModel,
  ResponseModels_Customers_ChamberOfCommerce_ChamberOfCommerceSearchResponseModel,
} from '@monorepo-types/dc';
import { assign, setup, fromPromise } from 'xstate';

import { FlowContextSme } from '../context';

export interface ChamberOfCommerceSearchMachineContext {
  results?: Exclude<Customers_ChamberOfCommerce_ChamberOfCommerceSearchResponseModel['results'], undefined>;

  errorMessage?: string;

  lastCallData?: {
    cachedResponse?: ResponseModels_Customers_ChamberOfCommerce_ChamberOfCommerceSearchResponseModel;
    eventValues: FlowContextSme;
  };
}

export interface ChamberOfCommerceSearchMachineEvent {
  type: 'GET_COMPANY_OPTIONS' | 'IDLE' | 'NOT_FOUND';
  config: MachineConfig;
  values: FlowContextSme;
}

const chamberOfCommerceSearchService = (
  config: MachineConfig,
  values: FlowContextSme,
): Promise<{
  data?: ResponseModels_Customers_ChamberOfCommerce_ChamberOfCommerceSearchResponseModel;
  values?: FlowContextSme;
}> =>
  new Promise((resolve, reject) => {
    const requestBody: RequestModels_Customers_ChamberOfCommerce_ChamberOfCommerceSearchRequestModel = {
      data: {
        name: values.companyName || '',
        page: 1,
        amount: 10,
      },
    };

    return chamberOfCommerceSearch({
      businessUnit: config.businessUnit,
      label: config.label,
      requestBody,
    })
      .then(response => {
        resolve({ data: response, values });
      })
      .catch((error: ApiError) => {
        reject(new Error(error?.message));
      });
  });

const actors = {
  get: fromPromise(
    async ({
      input,
    }: {
      input: { context: ChamberOfCommerceSearchMachineContext; event: ChamberOfCommerceSearchMachineEvent };
    }) => {
      const { context, event } = input;
      const { config, values } = event;
      const { lastCallData } = context;

      const flowContextKeys: Array<keyof FlowContextSme> = ['companyName'];
      const shouldFetch = !flowContextKeys.every(key => values[key] === lastCallData?.eventValues?.[key]);

      if (shouldFetch) {
        return await chamberOfCommerceSearchService(config, values);
      } else {
        return { data: lastCallData?.cachedResponse, values: lastCallData?.eventValues };
      }
    },
  ),
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const chamberOfCommerceSearchMachine: any = setup({
  types: {} as {
    context: ChamberOfCommerceSearchMachineContext;
    events: ChamberOfCommerceSearchMachineEvent;
  },
  actors,
}).createMachine({
  id: 'chamberOfCommerceSearch',
  initial: 'IDLE',
  context: {},
  on: {
    GET_COMPANY_OPTIONS: { target: '.FETCHING' },
    IDLE: { target: '.IDLE' },
  },
  states: {
    IDLE: {},
    FETCHING: {
      invoke: {
        src: 'get',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: 'SUCCESS',
          actions: [
            assign(({ context, event }) => ({
              ...context,
              results: event?.output?.data?.data?.results,
              lastCallData: {
                eventValues: {
                  companyName: event?.output?.values?.companyName,
                },
                cachedResponse: event?.output?.data,
              },
            })),
          ],
        },
        onError: [
          {
            target: 'ERROR_NOT_FOUND',
            guard: ({ event }) => (event as { error?: Error })?.error?.message === 'Not Found',
            actions: [
              assign(({ context, event }) => ({
                ...context,
                errorMessage: (event as { error?: Error })?.error?.message,
              })),
            ],
          },
          {
            target: 'ERROR',
            actions: [
              assign(({ context, event }) => ({
                ...context,
                errorMessage: (event as { error?: Error })?.error?.message,
              })),
            ],
          },
        ],
      },
    },
    SUCCESS: {
      entry: [
        assign(({ context }) => ({
          ...context,
          errorMessage: undefined,
        })),
      ],
      after: {
        500: { target: 'IDLE' },
      },
    },
    ERROR: {},
    ERROR_NOT_FOUND: {},
  },
});
